<ng-container *ngIf="!disableContentPage; else contentWithoutWrapper">
  <gatewee-content-page
    [breadcrumb]="resolvedBreadcrumb"
    [breadcrumbMode]="'manual'"
    [title]="'features.tasks.title' | translate"
  >
    <div page-body class="gw-flex gw-flex-col gw-gap-6">
      <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
    </div>
  </gatewee-content-page>
</ng-container>

<ng-template #contentWithoutWrapper>
  <div class="gw-flex gw-flex-col gw-gap-6">
    <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
  </div>
</ng-template>

<ng-template #contentTemplate>
  <p class="gw-text-sm gw-text-text-secondary gw-m-0">
    {{ 'features.tasks.subtitle' | translate }}
  </p>

  <section class="gw-flex gw-flex-col gw-gap-medium">
    <h3
      class="gw-flex gw-items-center gw-gap-2x-small gw-text-base gw-font-semibold gw-text-text-primary gw-m-0"
    >
      <gatewee-icon
        icon="@phi.list-checks"
        iconStyle="primary"
        size="20px"
      ></gatewee-icon>
      {{ 'features.tasks.activeSection' | translate }}
    </h3>

    <ng-container *ngIf="activeTasks.length > 0; else noActiveTasks">
      <div class="gw-grid gw-grid-cols-1 lg:gw-grid-cols-2 gw-gap-medium">
        <div
          *ngFor="let task of activeTasks"
          class="gw-bg-background-elevation-3 gw-border-base gw-rounded-2xl gw-p-medium gw-overflow-hidden"
        >
          <div
            class="gw-flex gw-justify-between gw-items-start gw-gap-small gw-mb-medium"
          >
            <div class="gw-flex gw-gap-small gw-min-w-0">
              <div
                class="gw-w-12 gw-h-12 gw-rounded-xl gw-bg-background-accent-1-pale gw-flex gw-items-center gw-justify-center gw-text-primary gw-border gw-border-border-normal gw-flex-shrink-0"
              >
                <gatewee-icon
                  [icon]="getTaskIcon(task.type)"
                  iconStyle="primary"
                  size="24px"
                ></gatewee-icon>
              </div>
              <div class="gw-min-w-0">
                <h4
                  class="gw-text-base gw-font-semibold gw-text-text-primary gw-m-0 gw-truncate"
                >
                  {{ getTaskLabel(task) }}
                </h4>
                <p class="gw-text-xs gw-text-text-secondary gw-m-0">
                  <ng-container
                    *ngIf="getTaskSubtitle(task) as subtitle; else taskId"
                    >{{ subtitle }}</ng-container
                  >
                  <ng-template #taskId>ID: #{{ task.id }}</ng-template>
                </p>
              </div>
            </div>
            <span
              class="gw-text-xs gw-font-semibold gw-px-2x-small gw-py-3x-small gw-rounded-md gw-capitalize gw-flex-shrink-0"
              [ngClass]="{
                'gw-bg-status-warning-pale gw-text-status-warning gw-animate-pulse':
                  task.status === 'processing',
                'gw-bg-background-neutral-1 gw-text-text-secondary':
                  task.status === 'pending'
              }"
            >
              {{ getStatusLabel(task.status) }}
            </span>
          </div>

          <div class="gw-flex gw-flex-col gw-gap-2x-small">
            <div
              class="gw-flex gw-justify-between gw-items-baseline gw-gap-small gw-text-sm gw-text-text-secondary"
            >
              <span class="gw-tabular-nums">
                {{ 'features.tasks.progress' | translate }}:
                {{ getTaskProgress(task) }}%
              </span>
              <span
                *ngIf="getTaskEta(task) as eta"
                class="gw-text-brand-primary gw-tabular-nums gw-shrink-0"
              >
                {{ eta }}
              </span>
            </div>
            <div
              class="task-progress-track"
              [class.is-processing]="task.status === 'processing'"
              role="progressbar"
              [attr.aria-valuenow]="getTaskProgress(task)"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div
                *ngIf="task.status === 'processing'"
                class="task-progress-track-loader"
                aria-hidden="true"
              ></div>
              <div
                class="task-progress-fill"
                [class.is-processing]="task.status === 'processing'"
                [style.width.%]="getTaskProgress(task)"
              ></div>
            </div>
          </div>

          <div class="gw-mt-large gw-flex gw-gap-small">
            <button
              type="button"
              class="gw-flex-1 gw-py-2x-small gw-text-sm gw-font-semibold gw-text-text-secondary gw-border gw-border-border-normal gw-rounded-lg gw-bg-transparent gw-cursor-not-allowed gw-opacity-60"
              disabled
              [tuiHint]="comingSoonHint | translate"
              tuiHintDirection="top"
            >
              {{ 'features.tasks.actions.pause' | translate }}
            </button>
            <button
              type="button"
              class="gw-flex-1 gw-py-2x-small gw-text-sm gw-font-semibold gw-text-status-negative gw-border gw-border-status-negative/30 gw-rounded-lg gw-bg-transparent gw-cursor-not-allowed gw-opacity-60"
              disabled
              [tuiHint]="comingSoonHint | translate"
              tuiHintDirection="top"
            >
              {{ 'features.tasks.actions.cancel' | translate }}
            </button>
          </div>
        </div>
      </div>
    </ng-container>

    <ng-template #noActiveTasks>
      <div
        class="gw-bg-background-elevation-3 gw-border-base gw-rounded-2xl gw-p-large gw-text-center"
      >
        <p class="gw-text-sm gw-text-text-secondary gw-m-0">
          {{ 'features.tasks.empty.active' | translate }}
        </p>
      </div>
    </ng-template>
  </section>

  <section class="gw-flex gw-flex-col gw-gap-medium">
    <div
      class="gw-flex gw-flex-col md:gw-flex-row md:gw-items-center gw-justify-between gw-gap-medium"
    >
      <h3 class="gw-text-base gw-font-semibold gw-text-text-primary gw-m-0">
        {{ 'features.tasks.historySection' | translate }}
      </h3>
      <div class="gw-flex gw-flex-wrap gw-gap-2x-small">
        <button
          type="button"
          class="gw-cursor-pointer gw-px-medium gw-py-2x-small gw-rounded-full gw-text-xs gw-font-bold gw-transition-colors"
          [ngClass]="
            historyStatusFilter === 'all'
              ? 'gw-bg-background-accent-1 gw-text-text-primary-on-accent-1'
              : 'gw-bg-background-neutral-1 gw-text-text-secondary hover:gw-bg-background-neutral-1-hover'
          "
          (click)="setHistoryFilter('all')"
        >
          {{ 'features.tasks.filters.all' | translate }}
        </button>
        <button
          type="button"
          class="gw-cursor-pointer gw-px-medium gw-py-2x-small gw-rounded-full gw-text-xs gw-font-bold gw-transition-colors"
          [ngClass]="
            historyStatusFilter === 'completed'
              ? 'gw-bg-background-accent-1 gw-text-text-primary-on-accent-1'
              : 'gw-bg-background-neutral-1 gw-text-text-secondary hover:gw-bg-background-neutral-1-hover'
          "
          (click)="setHistoryFilter('completed')"
        >
          {{ 'features.tasks.filters.completed' | translate }}
        </button>
        <button
          type="button"
          class="gw-cursor-pointer gw-px-medium gw-py-2x-small gw-rounded-full gw-text-xs gw-font-bold gw-transition-colors"
          [ngClass]="
            historyStatusFilter === 'failed'
              ? 'gw-bg-background-accent-1 gw-text-text-primary-on-accent-1'
              : 'gw-bg-background-neutral-1 gw-text-text-secondary hover:gw-bg-background-neutral-1-hover'
          "
          (click)="setHistoryFilter('failed')"
        >
          {{ 'features.tasks.filters.failed' | translate }}
        </button>
      </div>
    </div>

    <ng-template #taskTpl let-row>
      <div class="gw-flex gw-items-center gw-gap-small gw-min-w-0">
        <gatewee-icon
          [icon]="getTaskIcon(row.type)"
          iconStyle="primary"
          size="18px"
          class="gw-text-text-secondary gw-flex-shrink-0"
        ></gatewee-icon>
        <div class="gw-min-w-0">
          <p
            class="gw-text-sm gw-font-semibold gw-text-text-primary gw-m-0 gw-truncate"
          >
            {{ getTaskLabel(row) }}
          </p>
          <p class="gw-text-[10px] gw-text-text-secondary gw-m-0">
            {{ getTaskSubtitle(row) || getTaskTypeLabel(row.type) }}
          </p>
        </div>
      </div>
    </ng-template>

    <ng-template #durationTpl>
      <span class="gw-text-sm gw-text-text-secondary">—</span>
    </ng-template>

    <ng-template #statusTpl let-row>
      <span
        class="gw-inline-flex gw-items-center gw-gap-1.5 gw-p-small gw-py-3x-small gw-rounded-full gw-text-xs gw-font-bold gw-capitalize"
        [ngClass]="getStatusBadgeClass(row.status)"
      >
        <span
          class="gw-w-1.5 gw-h-1.5 gw-rounded-full"
          [ngClass]="{
            'gw-bg-status-positive': row.status === 'completed',
            'gw-bg-status-negative': row.status === 'failed',
            'gw-bg-status-warning': row.status === 'processing',
            'gw-bg-text-secondary': row.status === 'pending'
          }"
        ></span>
        {{ getStatusLabel(row.status) }}
      </span>
    </ng-template>

    <gatewee-table-v2
      *ngIf="columns.length && historyTasks.length > 0"
      [data]="historyTasks"
      [columns]="columns"
      [page]="page"
      [pageSize]="pageSize"
      [total]="total"
      [showSettings]="false"
      [tableId]="'tasks-history-' + scope"
      [getActionDisabled]="getActionDisabled"
      (outClick)="onActionClicked($event)"
      (newPage)="onPageChange($event)"
      (pageSizeChange)="onPageSizeChange($event)"
    ></gatewee-table-v2>

    <div
      *ngIf="historyTasks.length === 0"
      class="gw-border-base gw-bg-background-elevation-2 gw-rounded-2xl gw-text-center gw-text-text-secondary gw-py-12 gw-px-4"
    >
      <p class="gw-text-sm gw-m-0">
        {{ 'features.tasks.empty.history' | translate }}
      </p>
    </div>
  </section>
</ng-template>
